import { useState, useCallback } from "react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { QuotePipelineCard } from "./QuotePipelineCard";
import { PIPELINE_STAGES, PIPELINE_STAGE_LABELS, PIPELINE_STAGE_COLORS, type PipelineStage } from "@/types/quotes";
import { useUpdatePipelineStage, useToggleLost, type QuoteRow } from "@/hooks/useQuotes";
import { ChevronDown } from "lucide-react";

const INITIAL_VISIBLE = 10;
const LOAD_MORE_INCREMENT = 10;

interface QuotePipelineViewProps {
  quotes: QuoteRow[];
  onViewQuote: (id: string) => void;
  onContactClick?: (contactId: string) => void;
  /** Include quotes marked lost. The toggle lives in the page toolbar (#708). */
  showLost: boolean;
}

const ACTIVE_STAGES = PIPELINE_STAGES.filter((s) => s !== "won");
const ARCHIVE_STAGES: PipelineStage[] = ["won"];

export function QuotePipelineView({ quotes, onViewQuote, onContactClick, showLost }: QuotePipelineViewProps) {
  const updateStage = useUpdatePipelineStage();
  const toggleLost = useToggleLost();
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);
  const [visibleCounts, setVisibleCounts] = useState<Record<string, number>>({});

  const getVisibleCount = (stage: string) => visibleCounts[stage] ?? INITIAL_VISIBLE;
  const showMore = useCallback((stage: string) => {
    setVisibleCounts((prev) => ({
      ...prev,
      [stage]: (prev[stage] ?? INITIAL_VISIBLE) + LOAD_MORE_INCREMENT,
    }));
  }, []);

  const getQuotesForStage = (stage: string) =>
    quotes.filter((q) => q.pipeline_stage === stage && (showLost || !q.is_lost));

  const handleDrop = (e: React.DragEvent, stage: string) => {
    e.preventDefault();
    setDragOverStage(null);
    const quoteId = e.dataTransfer.getData("text/plain");
    if (quoteId) {
      updateStage.mutate({ id: quoteId, pipeline_stage: stage });
    }
  };

  const handleDragOver = (e: React.DragEvent, stage: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverStage(stage);
  };

  const handleStageChange = (id: string, stage: string) => {
    updateStage.mutate({ id, pipeline_stage: stage });
  };

  const handleToggleLost = (id: string, markLost: boolean) => {
    toggleLost.mutate({ id, is_lost: markLost });
  };

  const archivedCount = ARCHIVE_STAGES.reduce((sum, s) => sum + quotes.filter((q) => q.pipeline_stage === s && !q.is_lost).length, 0);

  return (
    <div className="space-y-3">
      <ScrollArea className="w-full">
        <div className="flex gap-3 pb-4 min-w-max">
          {ACTIVE_STAGES.map((stage) => {
            const stageQuotes = getQuotesForStage(stage);
            const stageTotal = stageQuotes.reduce((sum, q) => sum + Number(q.total), 0);
            const isDragOver = dragOverStage === stage;

            return (
              <div
                key={stage}
                className={`w-[280px] shrink-0 flex flex-col rounded-lg border transition-colors ${
                  isDragOver ? "border-primary bg-primary/5" : "bg-muted/30"
                }`}
                onDrop={(e) => handleDrop(e, stage)}
                onDragOver={(e) => handleDragOver(e, stage)}
                onDragLeave={() => setDragOverStage(null)}
              >
                {/* Column Header */}
                <div className="px-3 py-2 border-b flex items-center gap-2">
                  <Badge className={`${PIPELINE_STAGE_COLORS[stage]} text-[10px] px-1.5 py-0`}>
                    {PIPELINE_STAGE_LABELS[stage]}
                  </Badge>
                  <span className="text-xs text-muted-foreground font-medium">
                    {stageQuotes.length}
                  </span>
                  {stageTotal > 0 && (
                    <span className="ml-auto text-xs text-muted-foreground whitespace-nowrap">
                      ${stageTotal.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </span>
                  )}
                </div>

                {/* Cards */}
                <div className="p-2 space-y-2 flex-1 min-h-[120px]">
                  {stageQuotes.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-xs text-muted-foreground py-8">
                      No quotes
                    </div>
                  ) : (
                    <>
                      {stageQuotes.slice(0, getVisibleCount(stage)).map((q) => (
                        <QuotePipelineCard
                          key={q.id}
                          quote={q}
                          onClick={() => onViewQuote(q.id)}
                          onContactClick={onContactClick}
                          onStageChange={handleStageChange}
                          onToggleLost={handleToggleLost}
                        />
                      ))}
                      {stageQuotes.length > getVisibleCount(stage) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full text-xs text-muted-foreground hover:text-foreground"
                          onClick={() => showMore(stage)}
                        >
                          <ChevronDown className="h-3 w-3 mr-1" />
                          Show {Math.min(LOAD_MORE_INCREMENT, stageQuotes.length - getVisibleCount(stage))} more
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      {/* Archived stages summary */}
      {archivedCount > 0 && (
        <div className="flex gap-4 text-xs text-muted-foreground">
          {ARCHIVE_STAGES.map((stage) => {
            const count = quotes.filter((q) => q.pipeline_stage === stage && !q.is_lost).length;
            if (count === 0) return null;
            return (
              <span key={stage} className="flex items-center gap-1.5">
                <Badge className={`${PIPELINE_STAGE_COLORS[stage]} text-[10px] px-1.5 py-0`}>
                  {PIPELINE_STAGE_LABELS[stage]}
                </Badge>
                {count} {count === 1 ? "quote" : "quotes"}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
